// Shared vocabulary for the SOW plan: statuses, dependency types, constraints,
// zoom levels and the small amount of date arithmetic the chart needs.
//
// The scheduling itself happens on the server (src/lib/schedule.js). Nothing
// here works out when a task starts; these helpers only turn dates the server
// has already decided into pixels and labels.

export const TASK_STATUSES = ['not_started', 'in_progress', 'completed', 'blocked', 'n_a'];
export const TASK_STATUS_LABELS = {
  not_started: 'Not Started', in_progress: 'In Progress', completed: 'Completed',
  blocked: 'Blocked', n_a: 'N/A',
};
export const TASK_STATUS_COLORS = {
  not_started: '#9CA3AF', in_progress: '#3B82F6', completed: '#10B981',
  blocked: '#EF4444', n_a: '#D1D5DB',
};

// Phases survive as an optional tag on a task. They are no longer the structure
// of the plan: the WBS is. Keeping them means existing plans, templates and the
// resource forecast carry on reading what they already read.
export const PHASES = [
  { id: 'initiation', label: 'Initiation', color: '#8B5CF6' },
  { id: 'planning',   label: 'Planning',   color: '#3B82F6' },
  { id: 'execution',  label: 'Execution',  color: '#10B981' },
  { id: 'close_out',  label: 'Close Out',  color: '#F59E0B' },
];
export const PHASE_MAP = Object.fromEntries(PHASES.map(p => [p.id, p]));

export const DEP_TYPES = [
  { id: 'FS', label: 'Finish to Start', short: 'FS', hint: 'starts after the predecessor finishes' },
  { id: 'SS', label: 'Start to Start',  short: 'SS', hint: 'starts after the predecessor starts' },
  { id: 'FF', label: 'Finish to Finish',short: 'FF', hint: 'finishes after the predecessor finishes' },
  { id: 'SF', label: 'Start to Finish', short: 'SF', hint: 'finishes after the predecessor starts' },
];

export const CONSTRAINT_TYPES = [
  { id: 'asap', label: 'As soon as possible', needsDate: false, hint: 'Follows its predecessors. The normal setting.' },
  { id: 'snet', label: 'Start no earlier than', needsDate: true, hint: 'Held back until this date even if the logic would allow earlier.' },
  { id: 'mso',  label: 'Must start on', needsDate: true, hint: 'Pinned. Warns if the predecessors cannot deliver it.' },
  { id: 'fnlt', label: 'Finish no later than', needsDate: true, hint: 'A deadline. Flags a breach, never drags the task earlier.' },
];
export const CONSTRAINT_MAP = Object.fromEntries(CONSTRAINT_TYPES.map(c => [c.id, c]));

/* ── Zoom ─────────────────────────────────────────────────────
   Owen: "This needs the ability to zoom out so you can see more
   or all of the plan at once."
   ─────────────────────────────────────────────────────────── */
export const ZOOM_LEVELS = [
  { id: 'day',     label: 'Day',     px: 34,  minor: 'day',     major: 'week'    },
  { id: 'week',    label: 'Week',    px: 13,  minor: 'week',    major: 'month'   },
  { id: 'month',   label: 'Month',   px: 4.2, minor: 'month',   major: 'quarter' },
  { id: 'quarter', label: 'Quarter', px: 1.6, minor: 'quarter', major: 'year'    },
  { id: 'year',    label: 'Year',    px: 0.5, minor: 'year',    major: 'year'    },
];
export const ZOOM_MAP = Object.fromEntries(ZOOM_LEVELS.map(z => [z.id, z]));

/* ── Dates ────────────────────────────────────────────────── */

const MS_DAY = 86400000;

export const toUTC = (s) => {
  if (!s) return null;
  const [y, m, d] = String(s).slice(0, 10).split('-').map(Number);
  return Date.UTC(y, m - 1, d);
};
export const toISO = (ms) => new Date(ms).toISOString().slice(0, 10);
export const addDays = (s, n) => (s ? toISO(toUTC(s) + Math.round(n) * MS_DAY) : null);
export const daysBetween = (a, b) => (a && b ? Math.round((toUTC(b) - toUTC(a)) / MS_DAY) : null);
export const dow = (s) => new Date(toUTC(s)).getUTCDay();
export const isWeekend = (s) => dow(s) === 0 || dow(s) === 6;

/** "12 Aug" */
export function fmtD(s) {
  if (!s) return '—';
  return new Date(toUTC(s)).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', timeZone: 'UTC' });
}
/** "12 Aug 2026" */
export function fmtDY(s) {
  if (!s) return '—';
  return new Date(toUTC(s)).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' });
}

/** "5d", "0.5d", "—" */
export function fmtDuration(d, unit) {
  if (d === null || d === undefined) return '—';
  const n = Number(d);
  if (!Number.isFinite(n)) return '—';
  if (n === 0) return '0d';
  return `${Number.isInteger(n) ? n : n.toFixed(1)}d${unit === 'calendar' ? ' cal' : ''}`;
}

/** "+3d late", "2d early", "on baseline" */
export function fmtVariance(v) {
  if (v === null || v === undefined) return null;
  if (v === 0) return 'on baseline';
  return v > 0 ? `${v}d late` : `${Math.abs(v)}d early`;
}

/** How a dependency reads in a predecessor list: "1.2 Survey FS+3d". */
export function fmtDepLabel(dep) {
  const lag = Number(dep.lag_days) || 0;
  if (!lag) return dep.dep_type || 'FS';
  const unit = dep.lag_unit === 'calendar' ? 'cd' : 'd';
  return `${dep.dep_type || 'FS'}${lag > 0 ? '+' : '−'}${Math.abs(lag)}${unit}`;
}

/* ── Colours ──────────────────────────────────────────────── */

// Distinct enough to tell apart at a glance, muted enough that twenty bars on
// one screen are not a fairground.
// No amber or orange: those belong to external work (EXTERNAL_BAR below), and
// a team drawn in them reads as an outside organisation at a glance.
export const TEAM_PALETTE = [
  '#6366F1', '#0EA5E9', '#10B981', '#0369A1', '#EC4899',
  '#8B5CF6', '#14B8A6', '#E11D48', '#84CC16', '#EF4444',
];

export function paletteFor(names = []) {
  const unique = [...new Set(names.filter(Boolean))];
  return Object.fromEntries(unique.map((n, i) => [n, TEAM_PALETTE[i % TEAM_PALETTE.length]]));
}

export const SCHEDULE_STATE = {
  ahead:       { label: 'Ahead',       color: '#059669', text: 'text-emerald-600', bg: 'bg-emerald-100 text-emerald-700' },
  on_track:    { label: 'On track',    color: '#0EA5E9', text: 'text-sky-600', bg: 'bg-sky-100 text-sky-700' },
  behind:      { label: 'Behind',      color: '#DC2626', text: 'text-red-600', bg: 'bg-red-100 text-red-700' },
  blocked:     { label: 'Blocked',     color: '#DC2626', text: 'text-red-600', bg: 'bg-red-100 text-red-700' },
  complete:    { label: 'Complete',    color: '#10B981', text: 'text-emerald-600', bg: 'bg-emerald-100 text-emerald-700' },
  not_started: { label: 'Not started', color: '#9CA3AF', text: 'text-gray-400', bg: 'bg-gray-100 text-gray-500' },
};

/** Who is on a task, as one short string. */
export function assigneeLabel(task) {
  const list = task.assignees || [];
  if (!list.length) return task.owner_name || '';
  const names = list.map(a => a.party_short_code || a.party_name || a.person_name).filter(Boolean);
  if (names.length <= 2) return names.join(', ');
  return `${names[0]} +${names.length - 1}`;
}

/** True when anybody on the task is an outside organisation. */
export function hasExternal(task) {
  return (task.assignees || []).some(a => a.external_party_id);
}

/** The outside organisations on a task, as short codes where they have one: "JLL, BT". */
export function externalLabel(task) {
  return (task.assignees || []).filter(a => a.external_party_id)
    .map(a => a.party_short_code || a.party_name).filter(Boolean).join(', ');
}

// External work is drawn in amber with dark hazard stripes, the one look on
// the chart that means "waiting on somebody we do not employ". No team is
// given amber, and the stripes and dashed edge make it plain in print too.
export const EXTERNAL_BAR = { fill: '#F59E0B', stripe: '#78350F', edge: '#B45309', tint: 'rgba(245,158,11,0.10)' };
